import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, ClickAwayListener, Drawer, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import colors from "../../../shared/themes/colors";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { NotificationMessage } from "../../../shared/models/models";
import ChevronDownIcon from "../../../shared/assets/icons/ChevronDownIcon";
import ActionToast from "../../../shared/components/ActionToast";
import Breadcrumbs, { BreadcrumbItem } from "../../../shared/components/Breadcrumbs";
import Loader from "../../../shared/components/Loader";
import { StockItemSupplier, StockItemSuppliersQueryParameter } from "../../shared/models/customerBuyerStockItemsModels";
import GroupedStockItemCard from "../../searchAndBuy/components/GroupedStockItemCard";
import { CustomerProductSuppliersState } from "../models/models";
import {
  getProduct,
  getSuppliers,
  getSuppliersFilters,
  changeQuery,
  clearSupplierItems,
  clearSuppliers,
  setError,
  setSuccess,
  selectSupplier
} from "../customerProductSuppliersSlice";
import ProductSupplierCard from "./ProductSupplierCard";
import ProductSupplierFilters from "./ProductSupplierFilters";
import ProductSupplierDetails from "./ProductSupplierDetails";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

interface SortOption {
  title: string;
  sortBy: StockItemSuppliersQueryParameter;
  sortDesc: boolean;
}

const ProductSuppliers = () => {
  const { query, product, suppliers, supplier, filters, filtersQuery, productLoading, suppliersLoading, success, error } =
    useAppSelector<CustomerProductSuppliersState>((state: any) => state.customerProductSuppliers);
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const search = searchParams.get("productNumber");
  const { productId, productNumber } = useParams();

  const moreItemsRef = useRef<any>(null);
  const hasMoreItems = () => {
    return !!suppliers && suppliers.items.length < suppliers.totalCount;
  };

  const { translate } = useTranslation("marketplace");
  const { translate: tCommon } = useTranslation("common");
  const sortOptions: SortOption[] = [
    { title: translate("defaultSort"), sortBy: StockItemSuppliersQueryParameter.name, sortDesc: false },
    { title: translate("priceLowToHigh"), sortBy: StockItemSuppliersQueryParameter.price, sortDesc: false },
    { title: translate("priceHighToLow"), sortBy: StockItemSuppliersQueryParameter.price, sortDesc: true }
  ];

  const PAGE_SIZE = 10;

  const breadcrumbItems: BreadcrumbItem[] = [
    { title: tCommon("buttons.search"), link: "/search" },
    { title: search, link: `/search?productNumber=${search}` },
    { title: product?.number }
  ];

  const showFilter =
    filters.maxPrice > 0 ||
    filters.countries?.length > 0 ||
    filters.conditions?.length > 0 ||
    filters.delivery?.length > 0 ||
    filters.groups?.length > 0;

  const handleSortChange = (event: SelectChangeEvent<number>) => {
    const sortOption = sortOptions[event.target.value as any];
    const newQuery = { ...query, sortBy: sortOption.sortBy, sortDesc: sortOption.sortDesc, pageNo: 1 };
    dispatch(changeQuery(newQuery));
    dispatch(clearSupplierItems(null));
    dispatch(getSuppliers({ productId: productId!, query: newQuery }));
  };

  const handleSelectSupplier = (item: StockItemSupplier) => {
    dispatch(selectSupplier(item));
  };

  const handleDiscardSupplier = () => {
    dispatch(selectSupplier(null));
  };

  useEffect(() => {
    const newQuery = {
      sortBy: StockItemSuppliersQueryParameter.name,
      sortDesc: false,
      pageNo: 1,
      pageSize: PAGE_SIZE,
      minPrice: filtersQuery.minPrice || undefined,
      maxPrice: filtersQuery.maxPrice,
      countryIds: filtersQuery.countryIds || [],
      deliveryCodes: filtersQuery.deliveryCodes || [],
      conditionCodes: filtersQuery.conditionCodes || [],
      groupIds: filtersQuery.groupIds || []
    };
    handleDiscardSupplier();
    dispatch(changeQuery(newQuery));
    dispatch(getProduct(productId!));
    dispatch(getSuppliersFilters({ productId: productId!, query: { ...filtersQuery } }));
    dispatch(clearSuppliers(null));
    dispatch(getSuppliers({ productId: productId!, query: newQuery }));
  }, [productId, filtersQuery]);

  useLayoutEffect(() => {
    if (moreItemsRef.current) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !suppliersLoading) {
            const newQuery = { ...query, pageNo: query.pageNo + 1 };
            dispatch(changeQuery(newQuery));
            dispatch(getSuppliers({ productId: productId!, query: newQuery }));
          }
        });
      });

      observer.observe(moreItemsRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [moreItemsRef.current, query, suppliers, suppliersLoading]);

  if (productLoading) {
    return <Loader />;
  }

  return (
    <>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Box className="w-full flex" sx={{ marginTop: "70px" }}>
        {showFilter && (<ProductSupplierFilters />)}

        <Box className="w-full px-4 pt-6 pb-4 v-fill">
          <Box className="mb-4">
            <Breadcrumbs items={breadcrumbItems} />
          </Box>

          <GroupedStockItemCard item={product} active={true} />

          {(suppliersLoading && !suppliers) ? (
            <Box className="flex justify-center w-full mt-4">
              <Loader />
            </Box>
          ) : (
            <>
              <Box className="mt-6 flex items-center gap-4">
                <Box className="text-16-bold" color={colors.secondary.gray20}>
                  {translate("weFound")} {suppliers?.totalCount} {suppliers?.totalCount === 1 ? translate("supplier") : translate("suppliers")}
                </Box>

                <Select
                  prefix={translate("sortBy")}
                  defaultValue={0}
                  onChange={handleSortChange}
                  size="small"
                  sx={{
                    height: "unset !important",
                    "& .MuiSvgIcon-root": {
                      color: dt.blue,
                      height: 12,
                      width: 12,
                      marginTop: "6px",
                      marginRight: "4px"
                    }
                  }}
                  IconComponent={ChevronDownIcon}
                  SelectDisplayProps={{
                    style: {
                      padding: "6.5px 38px 6.5px 16px"
                    }
                  }}
                  renderValue={(value) =>
                    <Box className="flex items-center gap-3">
                      <Box className="text-16-regular" color={colors.secondary.gray40}>{translate("sortBy")}</Box>
                      <Box>{sortOptions[value].title}</Box>
                    </Box>
                  }
                >
                  {sortOptions.map((option, i) => (
                    <MenuItem key={i} value={i}>
                      {option.title}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {(suppliersLoading && !suppliers?.items?.length) ? (
                <Box className="flex justify-center w-full mt-4">
                  <Loader />
                </Box>
              ) : (
                <>
                  <Box className="flex gap-2 flex-wrap mt-4">
                    {suppliers?.items.map((item) => (
                      <ProductSupplierCard
                        key={item.id}
                        supplier={item}
                        active={item.id === supplier?.id}
                        onSelect={(ev) => {
                          handleSelectSupplier(item);
                          ev.stopPropagation();
                        }}
                      />
                    ))}
                  </Box>

                  {hasMoreItems() && (
                    suppliersLoading ? (
                      <Box className="flex justify-center w-full mt-4">
                        <Loader />
                      </Box>
                    ) : (
                      <Box ref={moreItemsRef} />
                    )
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      <ClickAwayListener onClickAway={handleDiscardSupplier}>
        <Box className="absolute">
          <Drawer
            variant="temporary"
            anchor="right"
            open={!!supplier}
            onClose={handleDiscardSupplier}
            elevation={0}
            PaperProps={{
              sx: {
                background: "#FFF !important",
                borderLeft: "1px solid #D5D6D9",
                marginTop: "75px",
                height: "calc(100% - 75px)"
              }
            }}
            slotProps={{
              backdrop: {
                style: {
                  background: "#000",
                  opacity: 0.2
                }
              }
            }}
          >
            <ProductSupplierDetails
              productId={productId!}
              onClose={handleDiscardSupplier}
            />
          </Drawer>
        </Box>
      </ClickAwayListener>
    </>
  );
};

export default ProductSuppliers;